import React, { useEffect, useState } from "react";
import { NotificationService } from "../../services/NotificationService";
import {
  Notification,
  NotificationStatus,
  NotificationType,
} from "../../model_gen/notification";
import { usePermissions } from "../../PermissionsProvider";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { CompanyService } from "../../services/CompanyService";
import { Company } from "../../model_gen/company";
import { useWebSocket } from "../../utils/Websockets";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Skeleton } from "../ui/skeleton";
import {
  BellIcon,
  TrashIcon,
  CheckIcon,
  MoreHorizontalIcon,
  CheckSquareIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

interface AlertTrayProps {
  visible: boolean;
  onClose: () => void;
  refreshTrigger?: number;
  onNotificationsRead?: () => void;
}

const AlertTray: React.FC<AlertTrayProps> = ({
  visible,
  onClose,
  refreshTrigger = 0,
  onNotificationsRead,
}) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [markingAllRead, setMarkingAllRead] = useState<boolean>(false);
  const [company, setCompany] = useState<Company | null>(null);
  const [archivingAll, setArchivingAll] = useState<boolean>(false);
  const [hoveredNotificationId, setHoveredNotificationId] = useState<
    number | null
  >(null);

  // Fetch company for websocket key
  useEffect(() => {
    if (apiKey) {
      CompanyService.getCompany(null, apiKey).subscribe(
        (result: Company) => {
          setCompany(result);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [apiKey]);

  // Fetch notifications when drawer is opened or refreshTrigger changes
  useEffect(() => {
    if (visible) {
      fetchNotifications();
    }
  }, [visible, refreshTrigger]);

  const fetchNotifications = () => {
    setLoading(true);
    // Get all notifications that aren't deleted
    const notificationStatuses: NotificationStatus[] = [
      "CREATED",
      "ACKNOWLEDGED",
    ];
    const excludeTypes: NotificationType[] = ["FAULT"];

    NotificationService.getNotifications(
      notificationStatuses,
      [],
      excludeTypes,
      setLoading,
      apiKey
    ).subscribe(
      (result: Notification[]) => {
        // Add explicit type assertion to help TypeScript
        const sortedNotifications = [...(result || [])].sort((a, b) => {
          // First sort by status (CREATED comes before ACKNOWLEDGED)
          if (a.status === "CREATED" && b.status !== "CREATED") return -1;
          if (a.status !== "CREATED" && b.status === "CREATED") return 1;

          // Then sort by date (newest first)
          const dateA = a.date_created || 0;
          const dateB = b.date_created || 0;
          return dateB - dateA;
        }) as Notification[]; // Add type assertion

        setNotifications(sortedNotifications);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching notifications:", error);
        toast.error("Failed to load notifications");
        setLoading(false);
      }
    );
  };

  // Handle new notification from websocket
  const handleNewNotification = () => {
    if (visible) {
      // If drawer is open, refresh the notifications list
      fetchNotifications();
    }
  };

  // Set up websocket listener for notifications
  useWebSocket({
    key: company?.id,
    eventType: "NOTIFICATION",
    onMessage: handleNewNotification,
  });

  const handleOpenNotification = (notif: Notification) => {
    if (!notif.id) return;

    // Immediately navigate to the URL if it exists
    if (notif.url) {
      // Check if it's an internal URL or external URL
      if (
        notif.url.startsWith("/") ||
        notif.url.startsWith(window.location.origin)
      ) {
        // For internal URLs, use navigate
        // If it starts with the origin, remove the origin part
        const path = notif.url.startsWith(window.location.origin)
          ? notif.url.substring(window.location.origin.length)
          : notif.url;

        // Close the drawer before navigating
        onClose();

        // Navigate to the internal path
        navigate(path);
      } else {
        // For external URLs, open in new tab
        window.open(notif.url, "_blank");
      }
    }

    // Only update if notification is unread (CREATED)
    if (notif.status === "CREATED") {
      const updatedNotification = {
        ...notif,
        status: "ACKNOWLEDGED" as NotificationStatus,
      };

      NotificationService.updateNotificationStatus(
        updatedNotification as any,
        null,
        apiKey
      ).subscribe(
        () => {
          // Update the notification in the local state
          const updatedNotifications = notifications.map((n) =>
            n.id === notif.id ? { ...n, status: "ACKNOWLEDGED" } : n
          );

          // Re-sort the notifications after the status update
          const sortedNotifications = [...updatedNotifications].sort((a, b) => {
            if (a.status === "CREATED" && b.status !== "CREATED") return -1;
            if (a.status !== "CREATED" && b.status === "CREATED") return 1;

            const dateA = a.date_created || 0;
            const dateB = b.date_created || 0;
            return dateB - dateA;
          }) as Notification[]; // Add this type assertion

          setNotifications(sortedNotifications);

          // Call the callback to update the count in the header
          if (onNotificationsRead) {
            onNotificationsRead();
          }
        },
        (error: any) => {
          console.error("Error updating notification status:", error);
          toast.error("Failed to update notification status");
        }
      );
    }
  };

  const handleMarkAsRead = (event: React.MouseEvent, notif: Notification) => {
    event.stopPropagation();
    if (!notif.id || notif.status === "ACKNOWLEDGED") return;

    const updatedNotification = {
      ...notif,
      status: "ACKNOWLEDGED" as NotificationStatus,
    };

    NotificationService.updateNotificationStatus(
      updatedNotification as any,
      null,
      apiKey
    ).subscribe(
      () => {
        // Update the notification in the local state and re-sort
        const updatedNotifications = notifications.map((n) =>
          n.id === notif.id ? { ...n, status: "ACKNOWLEDGED" } : n
        );

        // Re-sort the notifications after the status update
        const sortedNotifications = [...updatedNotifications].sort((a, b) => {
          if (a.status === "CREATED" && b.status !== "CREATED") return -1;
          if (a.status !== "CREATED" && b.status === "CREATED") return 1;

          const dateA = a.date_created || 0;
          const dateB = b.date_created || 0;
          return dateB - dateA;
        }) as Notification[]; // Add this type assertion

        setNotifications(sortedNotifications);

        // Call the callback to update the count in the header
        if (onNotificationsRead) {
          onNotificationsRead();
        }
      },
      (error: any) => {
        console.error("Error marking notification as read:", error);
        toast.error("Failed to mark notification as read");
      }
    );
  };

  const handleDeleteNotification = (
    event: React.MouseEvent,
    notif: Notification
  ) => {
    event.stopPropagation();
    if (!notif.id) return;

    const updatedNotification = {
      ...notif,
      status: "DELETED" as NotificationStatus,
    };

    NotificationService.updateNotificationStatus(
      updatedNotification as any,
      null,
      apiKey
    ).subscribe(
      () => {
        // Remove the notification from the local state
        setNotifications(notifications.filter((n) => n.id !== notif.id));
      },
      (error: any) => {
        console.error("Error deleting notification:", error);
        toast.error("Failed to delete notification");
      }
    );
  };

  const handleMarkAllAsRead = () => {
    const unreadNotifications = notifications.filter(
      (notif) => notif.status === "CREATED"
    );

    if (unreadNotifications.length === 0) {
      return;
    }

    setMarkingAllRead(true);

    // Use Promise.all to update all unread notifications
    Promise.all(
      unreadNotifications.map((notif) => {
        const updatedNotification = {
          ...notif,
          status: "ACKNOWLEDGED" as NotificationStatus,
        };

        return new Promise((resolve, reject) => {
          NotificationService.updateNotificationStatus(
            updatedNotification as any,
            null,
            apiKey
          ).subscribe(
            () => resolve(notif.id),
            (error) => reject(error)
          );
        });
      })
    )
      .then(() => {
        // Update all notifications in the local state
        const updatedNotifications = notifications.map((notif) => {
          if (notif.status === "CREATED") {
            return { ...notif, status: "ACKNOWLEDGED" };
          }
          return notif;
        });

        // Re-sort the notifications after the status update
        const sortedNotifications = [...updatedNotifications].sort((a, b) => {
          // After marking all as read, sort by date (newest first)
          const dateA = a.date_created || 0;
          const dateB = b.date_created || 0;
          return dateB - dateA;
        }) as Notification[]; // Add this type assertion

        setNotifications(sortedNotifications);
        setMarkingAllRead(false);

        // Call the callback to update the count in the header
        if (onNotificationsRead) {
          onNotificationsRead();
        }
      })
      .catch((error) => {
        console.error("Error marking all as read:", error);
        toast.error("Failed to mark all notifications as read");
        setMarkingAllRead(false);
        // Refresh to get the current state
        fetchNotifications();
      });
  };

  const handleArchiveAll = () => {
    if (notifications.length === 0) {
      return;
    }

    setArchivingAll(true);

    // Use Promise.all to archive all notifications
    Promise.all(
      notifications.map((notif) => {
        const updatedNotification = {
          ...notif,
          status: "DELETED" as NotificationStatus,
        };

        return new Promise((resolve, reject) => {
          NotificationService.updateNotificationStatus(
            updatedNotification as any,
            null,
            apiKey
          ).subscribe(
            () => resolve(notif.id),
            (error) => reject(error)
          );
        });
      })
    )
      .then(() => {
        // Clear all notifications
        setNotifications([]);
        setArchivingAll(false);
      })
      .catch((error) => {
        console.error("Error archiving all notifications:", error);
        toast.error("Failed to archive all notifications");
        setArchivingAll(false);
        // Refresh to get the current state
        fetchNotifications();
      });
  };

  // New function to archive all read notifications
  const handleArchiveAllRead = () => {
    const readNotifications = notifications.filter(
      (notif) => notif.status === "ACKNOWLEDGED"
    );

    if (readNotifications.length === 0) {
      toast("No read notifications to archive");
      return;
    }

    setArchivingAll(true);

    // Use Promise.all to archive all read notifications
    Promise.all(
      readNotifications.map((notif) => {
        const updatedNotification = {
          ...notif,
          status: "DELETED" as NotificationStatus,
        };

        return new Promise((resolve, reject) => {
          NotificationService.updateNotificationStatus(
            updatedNotification as any,
            null,
            apiKey
          ).subscribe(
            () => resolve(notif.id),
            (error) => reject(error)
          );
        });
      })
    )
      .then(() => {
        // Remove archived notifications from the state
        setNotifications(
          notifications.filter((notif) => notif.status !== "ACKNOWLEDGED")
        );
        setArchivingAll(false);
      })
      .catch((error) => {
        console.error("Error archiving read notifications:", error);
        toast.error("Failed to archive read notifications");
        setArchivingAll(false);
        // Refresh to get the current state
        fetchNotifications();
      });
  };

  const getUnreadCount = () => {
    return notifications.filter((n) => n.status === "CREATED").length;
  };

  // Function to format the timestamp correctly
  const formatTimestamp = (timestamp: number | null | undefined): string => {
    if (!timestamp) {
      return "Unknown time";
    }

    // Check if timestamp is too small (missing digits)
    // Unix epoch in milliseconds should be ~13 digits for current dates
    // If it's much smaller, it's likely in seconds instead of milliseconds
    const currentTimeMs = Date.now();
    const expectedDigits = Math.floor(Math.log10(currentTimeMs)) + 1;
    const actualDigits = Math.floor(Math.log10(timestamp)) + 1;

    // If timestamp is in seconds, convert to milliseconds
    if (actualDigits < expectedDigits - 2) {
      timestamp = timestamp * 1000;
    }

    // Use moment to format the date
    return moment(timestamp).fromNow();
  };

  // Function to get read count
  const getReadCount = () => {
    return notifications.filter((n) => n.status === "ACKNOWLEDGED").length;
  };

  return (
    <Sheet open={visible} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[400px] p-0">
        <SheetHeader className="p-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <BellIcon className="w-5 h-5 text-maitai-green" />
              Notifications
              {getUnreadCount() > 0 && (
                <Badge variant="secondary" className="bg-maitai-green text-white">
                  {getUnreadCount()}
                </Badge>
              )}
            </SheetTitle>
            
            {/* Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={notifications.length === 0}
                >
                  <MoreHorizontalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleMarkAllAsRead}
                  disabled={getUnreadCount() === 0 || markingAllRead}
                  className="gap-2"
                >
                  <CheckSquareIcon className="w-4 h-4" />
                  Mark all as read
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleArchiveAll}
                  disabled={notifications.length === 0 || archivingAll}
                  className="gap-2"
                >
                  <TrashIcon className="w-4 h-4" />
                  Archive all
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleArchiveAllRead}
                  disabled={getReadCount() === 0 || archivingAll}
                  className="gap-2"
                >
                  <TrashIcon className="w-4 h-4" />
                  Archive all read
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Card key={i} className="border-border">
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-full" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center px-6">
              <div className="space-y-3">
                <h3 className="text-lg font-gc-med text-foreground">
                  No notifications
                </h3>
                <p className="text-muted-foreground max-w-md">
                  You're all caught up! New notifications will appear here.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {notifications.map((item) => (
                <Card
                  key={item.id}
                  className={cn(
                    "border-border cursor-pointer transition-all duration-200 hover:shadow-sm",
                    item.status === "CREATED" 
                      ? "bg-maitai-green/5 border-maitai-green/20" 
                      : "bg-card hover:bg-accent/20"
                  )}
                  onClick={() => handleOpenNotification(item)}
                  onMouseEnter={() => setHoveredNotificationId(item.id || null)}
                  onMouseLeave={() => setHoveredNotificationId(null)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 space-y-2">
                        <h4 className={cn(
                          "text-sm font-gc-reg text-foreground",
                          item.status === "CREATED" && "font-gc-med"
                        )}>
                          {item.title || "Notification"}
                        </h4>
                        <p className={cn(
                          "text-sm text-muted-foreground leading-relaxed",
                          item.status === "CREATED" && "text-foreground/80"
                        )}>
                          {item.content}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatTimestamp(item.date_created)}
                        </p>
                      </div>
                      
                      {/* Action buttons - only show on hover */}
                      {hoveredNotificationId === item.id && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {item.status === "CREATED" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={(e) => handleMarkAsRead(e, item)}
                            >
                              <CheckIcon className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={(e) => handleDeleteNotification(e, item)}
                          >
                            <TrashIcon className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer with quick actions */}
        {notifications.length > 0 && getUnreadCount() > 0 && (
          <>
            <Separator />
            <div className="p-6 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={markingAllRead}
                className="w-full gap-2"
              >
                <CheckIcon className="w-4 h-4" />
                Mark all as read
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AlertTray;
